import { useMemo } from 'react';
import { type NavigateOptions, useSearchParams } from 'react-router';
import {
  ZodDefault,
  type ZodObject,
  type ZodRawShape,
  type ZodTypeAny,
  type z,
} from 'zod';

type ParsedSearchParams<T extends ZodRawShape> = {
  [K in keyof T]: T[K] extends ZodDefault<ZodTypeAny>
    ? z.infer<T[K]>
    : z.infer<T[K]> | undefined;
};

export const useParsedSearchParams = <T extends ZodRawShape>(
  schema: ZodObject<T>,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedSearchParams = useMemo(() => {
    const parsedParams = {} as ParsedSearchParams<T>;
    for (const key in schema.shape) {
      const schemaField = schema.shape[key];
      const parsedValue = schemaField?.safeParse(
        searchParams.get(key) ?? undefined,
      );
      if (parsedValue?.success) {
        parsedParams[key] = parsedValue.data;
      } else if (schemaField instanceof ZodDefault) {
        parsedParams[key] = schemaField._def.defaultValue();
      } else {
        parsedParams[key] = undefined;
      }
    }

    return parsedParams;
  }, [searchParams, schema.shape]);

  const setSearchParamsByRecord = (
    params: Partial<
      Record<
        keyof ParsedSearchParams<T>,
        string | number | boolean | undefined | null
      >
    >,
    options?: NavigateOptions,
  ) => {
    const newSearchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        const stringValue =
          typeof value === 'boolean' ? String(value) : value.toString();
        newSearchParams.set(key, stringValue);
      }
    }

    setSearchParams(newSearchParams, options);
  };

  return {
    searchParams,
    setSearchParams,
    setSearchParamsByRecord,
    allSearchParams: parsedSearchParams,
    ...parsedSearchParams,
  };
};
